import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft, Car, Phone, Mail, MapPin, Calendar, TrendingUp,
  IndianRupee, Merge, Scissors, AlertCircle,
  CheckCircle, X, ExternalLink, Clock,
} from 'lucide-react'
import { customerService } from '../services/customers'
import type { CustomerDetail, CustomerLeadSummary, Vehicle } from '../../../types'
import { UserRole } from '../../../types'
import { authService } from '../../identity/services/auth'
import { LEAD_STATUS_COLORS, LEAD_STATUS_LABELS } from '../../../lib/constants'
import { ModalOverlay } from '../../../components/ModalOverlay'

// ── Merge Modal ───────────────────────────────────────────────────────────────

function MergeModal({
  canonicalId,
  onClose,
  onDone,
}: {
  canonicalId: number
  onClose: () => void
  onDone: (msg: string) => void
}) {
  const [sourceId, setSourceId] = useState('')
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState<string | null>(null)

  const handleMerge = async () => {
    const id = parseInt(sourceId, 10)
    if (!id || isNaN(id)) { setError('Enter a valid customer ID'); return }
    if (id === canonicalId) { setError('Cannot merge a customer into itself'); return }
    setLoading(true)
    setError(null)
    try {
      const res = await customerService.merge(canonicalId, { source_customer_id: id })
      onDone(
        `Merged customer #${res.tombstoned_id} into #${res.canonical_id}. ` +
        `${res.leads_repointed} lead(s) and ${res.vehicles_repointed} vehicle(s) re-linked.`
      )
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(typeof msg === 'string' ? msg : 'Merge failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
            Merge Duplicate Customer
          </h3>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            Enter the ID of the <strong>duplicate</strong> customer to tombstone. All their leads
            and vehicles will be re-linked to <strong>Customer #{canonicalId}</strong>.
          </p>

          <div className="form-group">
            <label className="form-label">Duplicate Customer ID</label>
            <input
              className="input"
              type="number"
              placeholder="e.g. 42"
              value={sourceId}
              onChange={e => setSourceId(e.target.value)}
            />
            <p className="form-hint">You can find the ID on the duplicate customer's profile URL.</p>
          </div>

          {error && (
            <div className="alert-danger">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-4 pb-4">
          <button className="btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn-primary" onClick={handleMerge} disabled={loading || !sourceId}>
            {loading ? 'Merging…' : 'Confirm Merge'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  )
}

// ── Split Modal ───────────────────────────────────────────────────────────────

function SplitModal({
  customer,
  onClose,
  onDone,
}: {
  customer: CustomerDetail
  onClose: () => void
  onDone: (msg: string) => void
}) {
  const [newName, setNewName]             = useState('')
  const [newMobile, setNewMobile]         = useState('')
  const [selectedLeads, setSelectedLeads]         = useState<Set<number>>(new Set())
  const [selectedVehicles, setSelectedVehicles]   = useState<Set<number>>(new Set())
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState<string | null>(null)

  const toggleLead = (id: number) => setSelectedLeads(prev => {
    const s = new Set(prev)
    s.has(id) ? s.delete(id) : s.add(id)
    return s
  })

  const toggleVehicle = (id: number) => setSelectedVehicles(prev => {
    const s = new Set(prev)
    s.has(id) ? s.delete(id) : s.add(id)
    return s
  })

  const handleSplit = async () => {
    if (!newName.trim()) { setError('Enter a name for the new customer'); return }
    if (selectedLeads.size === 0 && selectedVehicles.size === 0) {
      setError('Select at least one lead or vehicle to split off')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await customerService.split(customer.id, {
        new_customer_name: newName.trim(),
        ...(newMobile.trim() ? { new_customer_mobile: newMobile.trim() } : {}),
        lead_ids: [...selectedLeads],
        vehicle_ids: [...selectedVehicles],
      })
      onDone(
        `Split complete. New customer #${res.new_customer_id} created with ` +
        `${res.leads_moved} lead(s) and ${res.vehicles_moved} vehicle(s).`
      )
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(typeof msg === 'string' ? msg : 'Split failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
            Split Customer Record
          </h3>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            Select the leads and/or vehicles to move to a new customer record (e.g. a family
            member whose contact was used at the dealership).
          </p>

          <div className="form-group">
            <label className="form-label">New Customer Name</label>
            <input
              className="input"
              placeholder="Name for the split-off customer"
              value={newName}
              onChange={e => setNewName(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              New Customer Mobile <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              className="input"
              placeholder="Leave blank to reuse original mobile"
              value={newMobile}
              onChange={e => setNewMobile(e.target.value)}
            />
            <p className="form-hint">
              Provide if this is a different person (e.g. family member). Leave blank if splitting
              due to a data-entry error.
            </p>
          </div>

          {/* Vehicles */}
          {customer.vehicles.length > 0 && (
            <div>
              <p className="form-label mb-2">Vehicles</p>
              <div className="space-y-1">
                {customer.vehicles.map(v => (
                  <label key={v.id} className="flex items-center gap-2 cursor-pointer p-2 rounded-md hover:bg-surface-subtle">
                    <input
                      type="checkbox"
                      checked={selectedVehicles.has(v.id)}
                      onChange={() => toggleVehicle(v.id)}
                      className="rounded"
                    />
                    <Car className="h-4 w-4 shrink-0" style={{ color: 'var(--text-tertiary)' }} />
                    <span style={{ color: 'var(--text-primary)', fontSize: 13 }}>
                      {v.registration_number}
                      {v.model && <span style={{ color: 'var(--text-tertiary)' }}> · {v.model}</span>}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Leads */}
          {customer.leads.length > 0 && (
            <div>
              <p className="form-label mb-2">Leads</p>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {customer.leads.map(l => (
                  <label key={l.id} className="flex items-center gap-2 cursor-pointer p-2 rounded-md hover:bg-surface-subtle">
                    <input
                      type="checkbox"
                      checked={selectedLeads.has(l.id)}
                      onChange={() => toggleLead(l.id)}
                      className="rounded"
                    />
                    <span style={{ color: 'var(--text-primary)', fontSize: 13 }}>
                      #{l.id} · {l.vehicle_reg_no ?? '—'}
                    </span>
                    <span
                      className={`badge-${(LEAD_STATUS_COLORS[l.status] ?? 'neutral')}`}
                      style={{ marginLeft: 'auto', fontSize: 11 }}
                    >
                      {LEAD_STATUS_LABELS[l.status] ?? l.status}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="alert-danger">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-4 pb-4">
          <button className="btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn-primary" onClick={handleSplit} disabled={loading || !newName.trim()}>
            {loading ? 'Splitting…' : 'Confirm Split'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CustomerDetailPage() {
  const { id }      = useParams<{ id: string }>()
  const navigate    = useNavigate()
  const currentUser = authService.getCurrentUser()

  const canManage = [
    UserRole.INSURANCE_HEAD,
    UserRole.DEALER_PRINCIPAL,
    UserRole.BACKEND_ADMIN,
  ].includes(currentUser?.role as UserRole)

  const [customer, setCustomer] = useState<CustomerDetail | null>(null)
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState<string | null>(null)
  const [toast, setToast]       = useState<string | null>(null)

  const [showMerge, setShowMerge] = useState(false)
  const [showSplit, setShowSplit] = useState(false)

  const fetchCustomer = async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const data = await customerService.getById(Number(id))
      setCustomer(data)
    } catch {
      setError('Customer not found or you do not have access.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchCustomer() }, [id])

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 5000)
  }

  const handleActionDone = (msg: string) => {
    setShowMerge(false)
    setShowSplit(false)
    showToast(msg)
    fetchCustomer()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    )
  }

  if (error || !customer) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="alert-danger">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error ?? 'Customer not found'}</span>
        </div>
        <button className="btn-secondary mt-4" onClick={() => navigate('/customers')}>
          <ArrowLeft className="h-4 w-4" /> Back to Customers
        </button>
      </div>
    )
  }

  const yearsLabel = customer.years_as_customer < 1
    ? `${Math.round(customer.years_as_customer * 12)} months`
    : `${customer.years_as_customer.toFixed(1)} years`

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">

      {/* Toast */}
      {toast && (
        <div className="alert-success fixed top-4 right-4 z-50 max-w-sm shadow-lg">
          <CheckCircle className="h-4 w-4 shrink-0" />
          <span className="text-sm">{toast}</span>
          <button className="ml-auto opacity-60 hover:opacity-100" onClick={() => setToast(null)}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Back + header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <button className="btn-icon" onClick={() => navigate('/customers')}>
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="page-title">{customer.name}</h1>
            <p className="page-subtitle">Customer #{customer.id}</p>
          </div>
        </div>
        {canManage && customer.is_active && (
          <div className="flex items-center gap-2">
            <button className="btn-secondary" onClick={() => setShowMerge(true)}>
              <Merge className="h-3.5 w-3.5" /> Merge
            </button>
            <button className="btn-secondary" onClick={() => setShowSplit(true)}>
              <Scissors className="h-3.5 w-3.5" /> Split
            </button>
          </div>
        )}
      </div>

      {/* Merged tombstone banner */}
      {!customer.is_active && customer.merged_into_id && (
        <div className="alert-warning">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>
            This record was merged into{' '}
            <Link
              to={`/customers/${customer.merged_into_id}`}
              className="underline font-medium"
              style={{ color: 'var(--warning-text)' }}
            >
              Customer #{customer.merged_into_id}
            </Link>.
          </span>
        </div>
      )}

      {/* Contact + stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Contact card */}
        <div className="card space-y-3">
          <h2 className="font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>
            CONTACT DETAILS
          </h2>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 shrink-0" style={{ color: 'var(--brand-primary)' }} />
              <span style={{ fontFamily: 'monospace', fontSize: 14, color: 'var(--text-primary)' }}>
                {customer.mobile}
              </span>
            </div>
            {customer.email && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0" style={{ color: 'var(--text-tertiary)' }} />
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{customer.email}</span>
              </div>
            )}
            {customer.city && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0" style={{ color: 'var(--text-tertiary)' }} />
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{customer.city}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 shrink-0" style={{ color: 'var(--text-tertiary)' }} />
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                Customer since{' '}
                {new Date(customer.created_at).toLocaleDateString('en-IN', {
                  day: 'numeric', month: 'short', year: 'numeric',
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Journey metrics */}
        <div className="card">
          <h2 className="font-semibold text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
            CUSTOMER JOURNEY
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <MetricTile
              icon={<TrendingUp className="h-5 w-5" style={{ color: 'var(--brand-primary)' }} />}
              label="Total Leads"
              value={String(customer.total_leads)}
            />
            <MetricTile
              icon={<CheckCircle className="h-5 w-5" style={{ color: 'var(--success-text)' }} />}
              label="Renewals Done"
              value={String(customer.renewals_completed)}
            />
            <MetricTile
              icon={<IndianRupee className="h-5 w-5" style={{ color: 'var(--info-text)' }} />}
              label="Lifetime Premium"
              value={customer.lifetime_premium > 0
                ? `₹${customer.lifetime_premium.toLocaleString('en-IN')}`
                : '—'}
            />
            <MetricTile
              icon={<Clock className="h-5 w-5" style={{ color: 'var(--text-tertiary)' }} />}
              label="Years with Us"
              value={yearsLabel}
            />
          </div>
          {customer.last_contacted_at && (
            <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 8 }}>
              Last contacted:{' '}
              {new Date(customer.last_contacted_at).toLocaleDateString('en-IN', {
                day: 'numeric', month: 'short', year: 'numeric',
              })}
            </p>
          )}
        </div>
      </div>

      {/* Vehicles */}
      <div className="card">
        <h2 className="font-semibold text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
          VEHICLES ({customer.vehicles.length})
        </h2>
        {customer.vehicles.length === 0 ? (
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>No vehicles linked yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {customer.vehicles.map(v => (
              <VehicleCard key={v.id} vehicle={v} />
            ))}
          </div>
        )}
      </div>

      {/* Lead history */}
      <div className="card">
        <h2 className="font-semibold text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
          LEAD HISTORY ({customer.total_leads})
        </h2>
        {customer.leads.length === 0 ? (
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>No leads found.</p>
        ) : (
          <div className="table-card" style={{ margin: 0, boxShadow: 'none', border: '1px solid var(--border-default)' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Lead #</th>
                  <th>Vehicle</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Premium</th>
                  <th>Assigned To</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {customer.leads.map(l => (
                  <LeadRow key={l.id} lead={l} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showMerge && (
        <MergeModal
          canonicalId={customer.id}
          onClose={() => setShowMerge(false)}
          onDone={handleActionDone}
        />
      )}
      {showSplit && (
        <SplitModal
          customer={customer}
          onClose={() => setShowSplit(false)}
          onDone={handleActionDone}
        />
      )}
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function MetricTile({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div
      className="rounded-lg p-3 flex items-center gap-3"
      style={{ background: 'var(--surface-subtle)' }}
    >
      {icon}
      <div>
        <p style={{ fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </p>
        <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{value}</p>
      </div>
    </div>
  )
}

function VehicleCard({ vehicle: v }: { vehicle: Vehicle }) {
  return (
    <div
      className="rounded-lg p-3 flex items-start gap-3"
      style={{ border: '1px solid var(--border-default)' }}
    >
      <Car className="h-5 w-5 shrink-0 mt-0.5" style={{ color: 'var(--brand-primary)' }} />
      <div>
        <p className="font-semibold" style={{ fontSize: 13, color: 'var(--text-primary)', fontFamily: 'monospace' }}>
          {v.registration_number}
        </p>
        {(v.make || v.model) && (
          <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {[v.make, v.model].filter(Boolean).join(' ')}
          </p>
        )}
        {(v.year || v.fuel_type) && (
          <p style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
            {[v.year, v.fuel_type].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
    </div>
  )
}

function LeadRow({ lead: l }: { lead: CustomerLeadSummary }) {
  const navigate = useNavigate()
  const color    = LEAD_STATUS_COLORS[l.status] ?? 'neutral'
  const label    = LEAD_STATUS_LABELS[l.status]  ?? l.status

  return (
    <tr className="cursor-pointer" onClick={() => navigate(`/leads/${l.id}`)}>
      <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-tertiary)' }}>
        #{l.id}
      </td>
      <td style={{ fontSize: 13 }}>
        {l.vehicle_reg_no
          ? <span style={{ fontFamily: 'monospace' }}>{l.vehicle_reg_no}</span>
          : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
      </td>
      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        {l.insurance_due_date
          ? new Date(l.insurance_due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
          : '—'}
      </td>
      <td>
        <span className={`badge-${color}`} style={{ fontSize: 11 }}>
          {label}
        </span>
      </td>
      <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
        {l.premium_amount
          ? `₹${l.premium_amount.toLocaleString('en-IN')}`
          : '—'}
      </td>
      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        {l.assigned_to_name ?? '—'}
      </td>
      <td style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
        {new Date(l.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
      </td>
      <td>
        <ExternalLink className="h-3.5 w-3.5" style={{ color: 'var(--text-tertiary)' }} />
      </td>
    </tr>
  )
}
